import React, { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { LoginPage, SignupPage } from "@/pages/auth";
import { useAuthStore, initializeAuth } from "@/lib/api-functions/api-auth";
import { checkEnvironment } from "@/lib/api-functions/debug-api";
import { TooltipProvider } from "@/components/user-feedback/tooltip";
import { Toaster } from "@/components/user-feedback/toaster";
import { ConfirmationDialogProvider } from "@/components/user-feedback/confirmation-dialog";
import { SimplifiedUserDataProvider } from "@/providers/SimplifiedUserDataProvider";
import { RTLProvider } from "@/providers/RTLProvider";
import { GlossaryDataProvider } from "@/providers/ObjectsDataProvider";
import { GlossaryBlueprintsDataProvider } from "@/providers/GlossaryBlueprintsDataProvider";
import { GlobalLoadingProvider } from "@/lib/useObjectsData";
import { StringsDataProvider } from "@/providers/StringsDataProvider";
import { PageStructureProvider } from "@/providers/PageStructureProvider";
import { TypesDataProvider } from "@/providers/TypesDataProvider";
import { TagsDataProvider } from "@/providers/TagsDataProvider";
import { DocumentDataProvider } from "@/providers/DocumentDataProvider";
import { DialogDataProvider } from "@/providers/DialogDataProvider";
import { UIElementsDataProvider } from "@/providers/UIElementsDataProvider";
import { TextHighlightingProvider } from "@/providers/TextHighlightingProvider";
import { ThemeProvider } from "@/providers/ThemeProvider";
import { FavoritesDataProvider } from "@/providers/FavoritesDataProvider";
import AppLayout from "@/components/layout/AppLayout";
import HomePage from "@/pages/HomePage";
import DocumentsPage from "./pages/DocumentsPage";
import DocumentDetailPage from "./pages/DocumentDetailPage";
import TemplatePage from "./pages/TemplatePage";
import LibraryPage from "./pages/LibraryPage";
import DialogPage from "./pages/DialogPage";
import DialogItemPage from "./pages/DialogItemPage";
import BlueprintItemPage from "./pages/TemplateItemPage";
import TextManagementPage from "./pages/TextManagementPage";
import ImagesPage from "./pages/ImagesPage";
import RelationshipPage from "./pages/RelationshipPage";
import TMSPage from "./pages/TMSPage";
import TypesAndTagsPage from "./pages/TypesAndTagsPage";
import { LoadingSpinner } from "@/components/loaders/loading-spinner";
import SettingsPage from './pages/SettingsPage';
import DocumentBlockTestPage from './pages/test_pages/DocumentBlockTestPage';
import TextHighlightingTestPage from './pages/test_pages/TextHighlightingTestPage';
import SubscribePage from './pages/SubscribePage';
import FinishProfilePage from './pages/FinishProfilePage';
import UIPage from './pages/UIPage';
import ErrorBoundary from '@/components/ErrorBoundary';
import { updateLastOnline } from '@/lib/utils/lastOnlineTracker';
import ObjectPage from './pages/ObjectPage';
import ObjectMigrationPage from './pages/ObjectMigrationPage';
import TutorialPage from './pages/TutorialPage';
import FeatureNotAvailablePage from './pages/FeatureNotAvailablePage';
import { LicenseProtectedRoute } from '@/components/routing/LicenseProtectedRoute';
import PlaygroundPage from './pages/PlaygroundPage';
import { ZoomProvider } from '@/providers/ZoomProvider';
import LogicPage from './pages/LogicPage';
import LogicFlowDetailPage from './pages/LogicFlowDetailPage';
import FormPage from './pages/FormPage';
import VariablesPage from './pages/VariablesPage';
import { VariableDetailPage } from './pages/Variables';
import CodeEnginePage from './pages/CodeEnginePage';
import { DialogSectionProvider } from '@/contexts/DialogSectionContext';



const queryClient = new QueryClient();

const ProtectedRoute = ({ children, requiresCompleteProfile = false }: { children: React.ReactNode; requiresCompleteProfile?: boolean }) => {
  const { user, isAuthenticated, isLoading } = useAuthStore();

  // Check if there are magic link tokens in URL hash
  // If so, allow rendering (let the page process the tokens) instead of redirecting
  const hashParams = new URLSearchParams(window.location.hash.substring(1));
  const hasMagicLinkTokens = hashParams.get('access_token') && hashParams.get('refresh_token');

  // Show loading spinner while checking authentication (but not for magic links)
    if (isLoading) {
    return (
      <div className="flex flex-col min-h-screen w-full">
        <div className="flex-1 flex items-center justify-center">
          <LoadingSpinner
            size="lg"
            text="Checking authentication..."
          />
        </div>
      </div>
    );
  }

  // If magic link tokens are present, allow access (page will process them)
  if (hasMagicLinkTokens) {
    return <>{children}</>;
  }

  // Otherwise, require authentication
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  // Note: Profile completion check is handled in Login.tsx
  // Login already verifies full_name (from profiles table) + company membership
  // before allowing users to reach protected routes
  // No need to duplicate that check here with potentially stale auth store data

  return <>{children}</>;
};



const PublicOnlyRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isAuthenticated, isLoading, profile } = useAuthStore();

  if (isLoading) {
    return (
      <div className="flex flex-col min-h-screen w-full">
        <div className="flex-1 flex items-center justify-center">
          <LoadingSpinner
            size="lg"
            text="Checking authentication..."
          />
        </div>
      </div>
    );
  }

 /*  if (isAuthenticated && user) {
    // Check if user has completed their profile
    // If not, redirect to /finish-profile instead of /home
    if (!profile?.full_name) {
      return <Navigate to="/finish-profile" replace />;
    }
    return <Navigate to="/home" replace />;
  } */

  return <>{children}</>;
};

// Wrapper component that only renders Types, Tags, and Document providers when authenticated
const AuthenticatedProviders = ({ children }: { children: React.ReactNode }) => {
  const { user, isAuthenticated, isLoading } = useAuthStore();

  // Show loading while checking authentication
  if (isLoading) {
    return <>{children}</>;
  }

  // If authenticated, render with Types, Tags, and Document providers
  if (isAuthenticated && user?.id) {
    return (
      <TypesDataProvider>
        <TagsDataProvider>
          <DocumentDataProvider>
            {children}
          </DocumentDataProvider>
        </TagsDataProvider>
      </TypesDataProvider>
    );
  }

  // If not authenticated, render without providers
  return <>{children}</>;
};

// Wrapper to provide navigate to ErrorBoundary
const RoutesWithErrorBoundary = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  return (
    <ErrorBoundary navigate={navigate} fallbackPath="/home">
      {children}
    </ErrorBoundary>
  );
};


const App = () => {
  const { isAuthenticated, user, profile } = useAuthStore();

  useEffect(() => {
    // Initialize auth state
    initializeAuth();

    // Test API integration
    //runApiTests();
    // Debug API connection
    checkEnvironment();
    //debugApiConnection();
  }, []);

  // Set Sentry user context so feedback widget auto-fills name & email
  useEffect(() => {
    if (isAuthenticated && profile) {
      Sentry.setUser({
        id: profile.full_name || profile.id,
        email: profile.email, 
        username: profile.username || profile.full_name || undefined,
      });
    } else {
      Sentry.setUser({
        id: user?.id || undefined,
        email: user?.email || undefined,
        username: profile?.username || profile?.full_name || undefined,
      });
    }
  }, [isAuthenticated, profile, user]);

  // Track Last_Online for returning authenticated users (once per day)
  useEffect(() => {
    if (isAuthenticated && user) {
      // This will only update if not already updated today
      updateLastOnline().catch(err => {
        console.error('Failed to update Last_Online on app load:', err);
      });
    }
  }, [isAuthenticated, user]);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={100} skipDelayDuration={0}>
        <Toaster />
        <ConfirmationDialogProvider />
        <BrowserRouter future={{ v7_relativeSplatPath: true }}>
          <SimplifiedUserDataProvider>
            <RTLProvider>
              <ThemeProvider>
              <ZoomProvider>  
              <GlobalLoadingProvider>
              <GlossaryDataProvider>
                <DocumentDataProvider>
                  <DialogDataProvider>
                    <UIElementsDataProvider>
                      <GlossaryBlueprintsDataProvider>
                        <StringsDataProvider>
                        <PageStructureProvider>
                        <TextHighlightingProvider>
                          <FavoritesDataProvider>
                          <AuthenticatedProviders>
                              <RoutesWithErrorBoundary>
                                <Routes>
                                {/* Standalone routes (outside AppLayout) */}
                                <Route
                                  path="/login"
                                  element={
                                    <PublicOnlyRoute>
                                      <LoginPage />
                                    </PublicOnlyRoute>
                                  }
                                />
                                <Route
                                  path="/signup"
                                  element={
                                    <PublicOnlyRoute>
                                      <SignupPage />
                                    </PublicOnlyRoute>
                                  }
                                />
                                <Route
                                  path="/subscribe"
                                  element={<SubscribePage />}
                                />
                                <Route
                                  path="/finish-profile"
                                  element={<FinishProfilePage />}
                                />
                                <Route
                                  path="/feature-not-available"
                                  element={
                                    <ProtectedRoute>
                                      <FeatureNotAvailablePage />
                                    </ProtectedRoute>
                                  }
                                />

                                {/* Protected routes within AppLayout - only for authenticated users with complete profiles */}
                                <Route
                                  path="/"
                                  element={
                                    <ProtectedRoute requiresCompleteProfile={true}>
                                      <DialogSectionProvider>
                                        <AppLayout borderEffect="rgb" />
                                      </DialogSectionProvider>
                                    </ProtectedRoute>
                                  }
                                >
                                  {/* Root path redirect */}
                                  <Route
                                    index
                                    element={<Navigate to="/home" replace />}
                                  />
                                  <Route
                                    path="home"
                                    element={<HomePage />}
                                  />
                                  <Route
                                    path="tms"
                                    element={
                                      <LicenseProtectedRoute requiredFeature="tms">
                                        <TMSPage />
                                      </LicenseProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="text-management"
                                    element={
                                      <LicenseProtectedRoute requiredFeature="tms">
                                        <TextManagementPage />
                                      </LicenseProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="documents"
                                    element={
                                      <LicenseProtectedRoute requiredFeature="documents">
                                        <DocumentsPage />
                                      </LicenseProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="documents/:documentId"
                                    element={
                                      <LicenseProtectedRoute requiredFeature="documents">
                                        <DocumentDetailPage />
                                      </LicenseProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="templates"
                                    element={
                                      <LicenseProtectedRoute requiredFeature="templates">
                                        <TemplatePage />
                                      </LicenseProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="templates/:itemId"
                                    element={
                                      <LicenseProtectedRoute requiredFeature="templates">
                                        <BlueprintItemPage />
                                      </LicenseProtectedRoute>
                                    }
                                  />

                                  <Route
                                    path="library"
                                    element={
                                      <LicenseProtectedRoute requiredFeature="library">
                                        <LibraryPage />
                                      </LicenseProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="library/:itemId"
                                    element={
                                      <LicenseProtectedRoute requiredFeature="library">
                                        <ObjectPage />
                                      </LicenseProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="library/:objectId/migrate"
                                    element={
                                      <LicenseProtectedRoute requiredFeature="library">
                                        <ObjectMigrationPage />
                                      </LicenseProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="form"
                                    element={
                                      <LicenseProtectedRoute requiredFeature="library">
                                        <FormPage />
                                      </LicenseProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="dialogs"
                                    element={
                                      <LicenseProtectedRoute requiredFeature="dialogs">
                                        <DialogPage />
                                      </LicenseProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="dialogs/:dialogId"
                                    element={
                                      <LicenseProtectedRoute requiredFeature="dialogs">
                                        <DialogItemPage />
                                      </LicenseProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="images"
                                    element={
                                      <LicenseProtectedRoute requiredFeature="images">
                                        <ImagesPage />
                                      </LicenseProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="relationship"
                                    element={
                                      <LicenseProtectedRoute requiredFeature="relationships">
                                        <RelationshipPage />
                                      </LicenseProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="types-and-tags"
                                    element={
                                      <LicenseProtectedRoute requiredFeature="types">
                                        <TypesAndTagsPage />
                                      </LicenseProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="variables"
                                    element={
                                      <LicenseProtectedRoute requiredFeature="types">
                                        <VariablesPage />
                                      </LicenseProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="variables/:id"
                                    element={
                                      <LicenseProtectedRoute requiredFeature="types">
                                        <VariableDetailPage />
                                      </LicenseProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="settings"
                                    element={<SettingsPage />}
                                  />
                                  {/* Dev-only Code Engine route */}
                                  {import.meta.env.DEV && (
                                    <Route
                                      path="code-engine"
                                      element={<CodeEnginePage />}
                                    />
                                  )}
                                  <Route
                                    path="test/document-block"
                                    element={<DocumentBlockTestPage />}
                                  />
                                  <Route
                                    path="test/text-highlighting"
                                    element={<TextHighlightingTestPage />}
                                  />

                                  <Route
                                    path="ui"
                                    element={
                                      <LicenseProtectedRoute requiredFeature="ui">
                                        <UIPage />
                                      </LicenseProtectedRoute>
                                    }
                                  />
                                 
                                  <Route
                                    path="tutorial"
                                    element={<TutorialPage />}
                                  />
                                  <Route
                                    path="logic"
                                    element={
                                      <LicenseProtectedRoute requiredFeature="logic">
                                        <LogicPage />
                                      </LicenseProtectedRoute>
                                    }
                                  />
                                  <Route
                                    path="logic/:flowId"
                                    element={
                                      <LicenseProtectedRoute requiredFeature="logic">
                                        <LogicFlowDetailPage />
                                      </LicenseProtectedRoute>
                                    }
                                  />
                                  
                                  {/* Playground route - only available in development mode */}
                                  {import.meta.env.DEV && (
                                    <Route
                                      path="playground"
                                      element={<PlaygroundPage />}
                                    />
                                  )}

                                  {/* Catch-all for authenticated routes - redirect to home */}
                                  <Route path="*" element={<Navigate to="/home" replace />} />
                                </Route>

                                {/* Catch-all route - redirect unauthenticated users to login */}
                                <Route path="*" element={<Navigate to="/login" replace />} />
                                </Routes>
                              </RoutesWithErrorBoundary>
                          </AuthenticatedProviders>
                          </FavoritesDataProvider>
                        </TextHighlightingProvider>
                        </PageStructureProvider>
                        </StringsDataProvider>
                      </GlossaryBlueprintsDataProvider>
                    </UIElementsDataProvider>
                  </DialogDataProvider>
                </DocumentDataProvider>
              </GlossaryDataProvider>
            </GlobalLoadingProvider>
              </ZoomProvider>
              </ThemeProvider>
            </RTLProvider>
          </SimplifiedUserDataProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;